/* eslint-disable react-perf/jsx-no-new-function-as-prop */
import {type FC, useCallback, useState} from 'react';
import clsx from 'clsx';
import useEmblaCarousel from 'embla-carousel-react';

import {FullScreenIcon} from '@/modules/foundation/components/icons/full-screen-icon';
import type {ImageWeb} from '@/modules/foundation/components/image/types';
import type {KeyedArray} from '@/modules/foundation/shared/types';

import {ArrowLeftIcon} from '../icons/arrow-left-icon';
import {ArrowRightIcon} from '../icons/arrow-right-icon';
import {ImageBlock} from '../image/ImageBlock';

import {useDotButton} from './hooks/useDotButton';
import {usePrevNextButtons} from './hooks/usePrevNextButtons';
import {Lightbox} from './lightbox';

type Props = {
	images: KeyedArray<ImageWeb>;
	aspectRatio?: '1:1' | '4:3' | '3:2' | '16:9';
};

export const Gallery: FC<Props> = ({images, aspectRatio = '4:3'}) => {
	const [isOpen, setIsOpen] = useState(false);
	const handleOpenFullscreenClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		setIsOpen(true);
	}, []);

	const [emblaRef, emblaApi] = useEmblaCarousel({
		active: images.length > 1,
	});
	const {selectedIndex, onDotButtonClick} = useDotButton(emblaApi);
	const {prevBtnDisabled, nextBtnDisabled, onPrevButtonClick, onNextButtonClick} =
		usePrevNextButtons(emblaApi);
	return (
		<>
			<div className="group relative">
				<div
					className={clsx([
						'relative flex flex-col',
						aspectRatio === '1:1' && 'aspect-square',
						aspectRatio === '4:3' && 'aspect-[calc(4/3)]',
						aspectRatio === '3:2' && 'aspect-[calc(3/2)]',
						aspectRatio === '16:9' && 'aspect-video',
					])}
				>
					<div
						ref={emblaRef}
						className="flex shrink-0 grow overflow-hidden border border-border-soft bg-white"
					>
						<div className="flex grow touch-pan-y touch-pinch-zoom ">
							{images.map((image) => (
								<div
									key={image._key}
									className="relative flex shrink-0 grow-0 basis-full items-center justify-center"
								>
									<ImageBlock
										{...image.image}
										alt={image.altText ?? ''}
										style={{objectFit: 'fill', width: 'auto', height: '100%'}}
										className="p-4"
									/>
								</div>
							))}
						</div>
					</div>
				</div>

				{images.length > 1 && (
					<>
						<button
							type="button"
							onClick={onPrevButtonClick}
							disabled={prevBtnDisabled}
							aria-label="Forrige bilde"
							className="absolute left-0 top-1/2 -translate-y-1/2 rounded-full bg-[#e6e4e399] p-4 disabled:opacity-0  group-hover:bg-[#e6e4e399] group-hover:opacity-100 md:opacity-0"
						>
							<ArrowLeftIcon color="#261817" size={16} />
						</button>

						<button
							type="button"
							onClick={onNextButtonClick}
							disabled={nextBtnDisabled}
							aria-label="Neste bilde"
							className="absolute right-0 top-1/2 -translate-y-1/2 rounded-full bg-[#e6e4e399] p-4 disabled:opacity-0 group-hover:bg-[#e6e4e399] group-hover:opacity-100 md:opacity-0"
						>
							<ArrowRightIcon color="#261817" size={16} />
						</button>
						<div className="mt-4 flex justify-center gap-2">
							{images.map((image, index) => (
								<button
									key={image._key}
									type="button"
									onClick={() => onDotButtonClick(index)}
									className={clsx(
										'size-16 border-2 bg-white',
										selectedIndex === index
											? 'border-border-soft'
											: 'border-border-soft',
										'focus:outline-none',
									)}
								>
									{/* eslint-disable-next-line @next/next/no-img-element*/}
									<img
										src={image.image.src}
										alt={image.altText ?? ''}
										className="size-full object-cover"
									/>
								</button>
							))}
						</div>
					</>
				)}

				<div className={clsx('absolute', 'right-[10px]', 'top-[10px]', 'z-10')}>
					<button
						onClick={handleOpenFullscreenClick}
						className={clsx(
							'md:opacity-0',
							'group-hover:opacity-100',
							'rounded-[5px]',
							'bg-[#e6e4e399] group-hover:bg-[#e6e4e399]',
						)}
						aria-label="Åpne bildegalleri i fullskjerm"
					>
						<FullScreenIcon color="#261817" size={30} />
					</button>
				</div>
			</div>
			<Lightbox
				setIsOpen={setIsOpen}
				startIndex={selectedIndex}
				isOpen={isOpen}
				images={images}
			/>
		</>
	);
};
